<template>
  <div class="team-card-list">
    <article v-for="employee in team" :key="employee.id" class="team-row">
      <div class="team-row__top">
        <button :aria-label="$t('general.view_employee_detail', { name: employee.full_name || $t('general.employee') })"
                class="team-row__detail"
                type="button"
                @click="openDetail(employee)">
          <i aria-hidden="true" class="uil-info-circle"></i>
        </button>

        <div class="team-row__person">
          <UserProfileAvatar :name="employee.full_name || ''"
                             :photo="employee.avatar || ''"
                             class="team-row-avatar"/>
          <div class="team-row__info">
            <strong>{{ employee.full_name || "—" }}</strong>
            <span>{{ employee.email || "—" }}</span>
            <small>
              {{ $t('general.competency_count_label', { count: Number(employee.competencies_count) || 0 }) }}
            </small>
          </div>
        </div>
      </div>

      <div class="team-row__actions">
        <router-link :aria-label="$t('general.view_employee_competencies', { name: employee.full_name || $t('general.employee') })"
                     :to="{
            name: 'user.my-team.member',
            params: { userId: String(employee.id) },
          }"
                     class="team-row__action team-row__action--primary"
        >
          <i aria-hidden="true" class="uil-award"></i>
          <span>{{ $t('general.competencies') }}</span>
        </router-link>
        <button :aria-label="$t('general.view_employee_training', { name: employee.full_name || $t('general.employee') })"
                class="team-row__action team-row__action--edu"
                type="button"
                @click="$emit('open-training-status', employee)">
          <i aria-hidden="true" class="uil-graduation-hat"></i>
          <span>{{ $t('general.education') }}</span>
        </button>
      </div>
    </article>

    <AppModal :bottom-sheet="true"
              :title="detailModalTitle"
              :visible.sync="detailVisible"
              aria-modal="true"
              max-height="90vh"
              role="dialog"
              width="480px"
              @close="closeDetail">

      <template v-if="selectedEmployee">
        <section :aria-label="$t('general.employee_info')" class="detail-summary">
          <UserProfileAvatar :name="selectedEmployee.full_name || ''"
                             :photo="selectedEmployee.avatar || ''"
                             class="team-detail-avatar"/>
          <div>
            <strong>{{ selectedEmployee.full_name || "—" }}</strong>
            <span>{{ selectedEmployee.email || "—" }}</span>
          </div>
        </section>

        <dl class="detail-meta">
          <div v-for="field in detailFields" :key="field.key">
            <dt>{{ field.label }}</dt>
            <dd>{{ displayValue(selectedEmployee[field.key]) }}</dd>
          </div>
        </dl>
      </template>

      <template #footer>
        <button class="detail-close" type="button" @click="closeDetail">
          {{ $t('general.close') }}
        </button>
      </template>
    </AppModal>
  </div>
</template>

<script>
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import AppModal from "@/view/components/ui/AppModal.vue";

export default {
  name: "TeamCardList",
  components: {
    AppModal,
    UserProfileAvatar,
  },
  props: {
    team: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      detailVisible: false,
      selectedEmployee: null,
    };
  },
  computed: {
    detailFields() {
      return [
        { key: "region_name", label: this.$t("general.region") },
        { key: "department_name", label: this.$t("general.department") },
        { key: "branch_name", label: this.$t("general.branch") },
        { key: "position_name", label: this.$t("general.position") },
        { key: "title_name", label: this.$t("general.job_title") },
      ];
    },
    detailModalTitle() {
      const name = this.selectedEmployee?.full_name;
      return name
        ? this.$t("general.employee_detail_with_name", { name })
        : this.$t("general.employee_detail");
    },
  },
  methods: {
    displayValue(value) {
      return value === undefined || value === null || String(value).trim() === ""
          ? "—"
          : String(value);
    },
    openDetail(employee) {
      if (!employee) return;
      this.selectedEmployee = { ...employee };
      this.detailVisible = true;
    },
    closeDetail() {
      this.detailVisible = false;
      this.selectedEmployee = null;
    },
  },
};
</script>

<style scoped>
.team-card-list {
  display: none;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
}

.team-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-bottom: 1px solid #eef2f7;
}

.team-row:last-child {
  border-bottom: 0;
}

.team-row__top {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.team-row__detail {
  display: inline-flex;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  color: var(--backgroundColor, #666cff);
  background: rgba(102, 108, 255, 0.12);
  border: 1px solid rgba(102, 108, 255, 0.35);
  border-radius: 10px;
  cursor: pointer;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
}

.team-row__detail:active {
  color: #fff;
  background: var(--backgroundColor, #666cff);
  border-color: var(--backgroundColor, #666cff);
}

.team-row__detail i {
  font-size: 20px;
}

.team-row__person {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.team-row-avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  flex-shrink: 0;
  font-size: 13px;
}

.team-row__info {
  flex: 1;
  min-width: 0;
}

.team-row__info strong,
.team-row__info span,
.team-row__info small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-row__info strong {
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
}

.team-row__info span {
  margin-top: 2px;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.3;
}

.team-row__info small {
  margin-top: 4px;
  color: var(--backgroundColor, #3568d4);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
}

.team-row__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.team-row__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
}

.team-row__action:active {
  transform: scale(0.98);
}

.team-row__action i {
  font-size: 15px;
}

.team-row__action--primary {
  color: var(--backgroundColor, #666cff) !important;
  background: rgba(102, 108, 255, 0.07);
  border: 1px solid rgba(102, 108, 255, 0.25);
}

.team-row__action--primary:active {
  border-color: var(--backgroundColor, #666cff);
  background: rgba(102, 108, 255, 0.12);
}

.team-row__action--edu {
  margin: 0;
  color: #0f766e;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
}

.team-row__action--edu:active {
  color: #0f5f59;
  background: #ccfbf1;
  border-color: #2dd4bf;
}

.detail-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-bottom: 14px;
  background: #f8fafc;
  border: 1px solid #e6ebf2;
  border-radius: 10px;
}

.team-detail-avatar {
  width: 48px;
  height: 48px;
  min-width: 48px;
  flex-shrink: 0;
  font-size: 15px;
}

.detail-summary > div {
  min-width: 0;
}

.detail-summary strong,
.detail-summary span {
  display: block;
  overflow-wrap: anywhere;
}

.detail-summary strong {
  color: #1e293b;
  font-size: 15px;
}

.detail-summary span {
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
}

.detail-meta {
  display: grid;
  margin: 0;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  overflow: hidden;
}

.detail-meta > div {
  display: grid;
  grid-template-columns: minmax(0, 110px) minmax(0, 1fr);
  align-items: start;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #eef2f7;
}

.detail-meta > div:last-child {
  border-bottom: 0;
}

.detail-meta dt {
  min-width: 0;
  margin: 0;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  overflow-wrap: anywhere;
}

.detail-meta dd {
  min-width: 0;
  margin: 0;
  color: #1e293b;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.detail-close {
  min-height: 38px;
  padding: 0 17px;
  color: #475569;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
}

@media (max-width: 800px) {
  .team-card-list {
    display: block;
  }
}

@media (max-width: 639px) {
  .team-card-list {
    width: 100%;
    min-width: 0;
    border-radius: 12px;
  }

  .team-row {
    gap: 12px;
    padding: 12px;
  }

  .team-row__detail {
    flex-basis: 44px;
    width: 44px;
    height: 44px;
    min-width: 44px;
  }

  .team-row__info strong {
    font-size: 16px;
    line-height: 1.4;
  }

  .team-row__info span {
    font-size: 13px;
    line-height: 1.4;
  }

  .team-row__info small {
    font-size: 12px;
    line-height: 1.4;
  }

  .team-row__actions {
    gap: 8px;
  }

  .team-row__action {
    min-height: 48px;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 14px;
    line-height: 1.3;
  }

  .team-row__action i {
    font-size: 16px;
  }

  .detail-close {
    min-height: 48px;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .detail-meta > div {
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 10px 12px;
  }
}
</style>
